import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link"
import {
	INSERT_ORDERED_LIST_COMMAND,
	INSERT_UNORDERED_LIST_COMMAND,
	ListNode,
	REMOVE_LIST_COMMAND,
} from "@lexical/list"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { $createHeadingNode, HeadingNode } from "@lexical/rich-text"
import { $getSelectionStyleValueForProperty, $isAtNodeEnd, $patchStyleText, $setBlocksType } from "@lexical/selection"
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils"
import {
	IconBold,
	IconCode,
	IconHeading,
	IconItalic,
	IconLink,
	IconList,
	IconListNumbers,
	IconStrikethrough,
	IconSubscript,
	IconSuperscript,
	IconTextColor,
	IconUnderline,
} from "@tabler/icons-react"
import {
	$createParagraphNode,
	$getSelection,
	$isParagraphNode,
	$isRangeSelection,
	$isTextNode,
	COMMAND_PRIORITY_LOW,
	type ElementNode,
	FORMAT_TEXT_COMMAND,
	type RangeSelection,
	SELECTION_CHANGE_COMMAND,
	type TextFormatType,
	type TextNode,
} from "lexical"
import { type FC, useCallback, useEffect, useState } from "react"
import { HexColorInput, HexColorPicker } from "react-colorful"
import styled, { css } from "styled-components"
import { useDebouncedCallback } from "use-debounce"

import { toNullIfEmpty } from "../../utilities/string"
import Portal from "../Portal"
import Tooltip from "../Tooltip"

function setToolbarPosition(targetRect: DOMRect | null, floatingElem: HTMLElement): void {
	const verticalGap = 10
	const horizontalOffset = 5
	const scrollerElem = document.body.parentElement

	if (targetRect === null || !scrollerElem) {
		floatingElem.style.opacity = "0"
		floatingElem.style.transform = "translate(-10000px, -10000px)"
		return
	}

	const floatingElemRect = floatingElem.getBoundingClientRect()
	const anchorElementRect = document.body.getBoundingClientRect()
	const editorScrollerRect = scrollerElem.getBoundingClientRect()

	let top = targetRect.top - floatingElemRect.height - verticalGap
	let left = targetRect.left - horizontalOffset

	if (top < editorScrollerRect.top) {
		top += floatingElemRect.height + targetRect.height + verticalGap * 2
	}

	if (left + floatingElemRect.width > editorScrollerRect.right) {
		left = editorScrollerRect.right - floatingElemRect.width - horizontalOffset
	}

	top -= anchorElementRect.top
	left -= anchorElementRect.left

	floatingElem.style.opacity = "1"
	floatingElem.style.transform = `translate(${left}px, ${top}px)`
}

function getSelectedNode(selection: RangeSelection): TextNode | ElementNode {
	const anchorNode = selection.anchor.getNode()
	const focusNode = selection.focus.getNode()
	if (anchorNode === focusNode) return anchorNode

	if (selection.isBackward()) {
		return $isAtNodeEnd(selection.focus) ? anchorNode : focusNode
	} else {
		return $isAtNodeEnd(selection.anchor) ? anchorNode : focusNode
	}
}

type Props = { headings: boolean; colors: boolean }
const LexicalToolbarPlugin: FC<Props> = ({ headings, colors }) => {
	const [editor] = useLexicalComposerContext()

	const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null)

	const [isText, setText] = useState(false)
	const [isColorPickerOpen, setColorPickerOpen] = useState(false)
	const [isLinkInputOpen, setLinkInputOpen] = useState(false)
	const [linkInput, setLinkInput] = useState("")

	const [isHeading, setHeading] = useState(false)
	const [color, setColor] = useState<string | null>(null)
	const [isBold, setBold] = useState(false)
	const [isItalic, setItalic] = useState(false)
	const [isUnderline, setUnderline] = useState(false)
	const [isStrikethrough, setStrikethrough] = useState(false)
	const [isSubscript, setSubscript] = useState(false)
	const [isSuperscript, setSuperscript] = useState(false)
	const [isCode, setCode] = useState(false)
	const [listType, setListType] = useState<"bullet" | "number" | null>(null)
	const [isLink, setLink] = useState(false)

	const update = useCallback(() => {
		editor.getEditorState().read(() => {
			if (editor.isComposing()) return

			const selection = $getSelection()
			const nativeSelection = window.getSelection()
			const rootElement = editor.getRootElement()

			if (
				nativeSelection !== null &&
				(!$isRangeSelection(selection) ||
					rootElement === null ||
					!rootElement.contains(nativeSelection.anchorNode))
			) {
				setText(false)
				return
			}

			if (!$isRangeSelection(selection)) return

			const node = getSelectedNode(selection)
			const parentNode = node.getParent()
			if (selection.getTextContent() !== "") {
				setText($isTextNode(node) || $isParagraphNode(node))
			} else {
				setText(false)
			}

			const rawTextContent = selection.getTextContent().replace(/\n/g, "")
			if (!selection.isCollapsed() && rawTextContent === "") {
				setText(false)
				return
			}

			setBold(selection.hasFormat("bold"))
			setItalic(selection.hasFormat("italic"))
			setUnderline(selection.hasFormat("underline"))
			setStrikethrough(selection.hasFormat("strikethrough"))
			setSubscript(selection.hasFormat("subscript"))
			setSuperscript(selection.hasFormat("superscript"))
			setCode(selection.hasFormat("code"))
			setColor(toNullIfEmpty($getSelectionStyleValueForProperty(selection, "color")))

			if ($isLinkNode(node)) {
				setLink(true)
				setLinkInput(node.getURL())
			} else if (parentNode && $isLinkNode(parentNode)) {
				setLink(true)
				setLinkInput(parentNode.getURL())
			} else {
				setLink(false)
				setLinkInput("")
			}

			const parentHeading = $getNearestNodeOfType<HeadingNode>(node, HeadingNode)
			setHeading(node instanceof HeadingNode || parentHeading !== null)

			const parentList = $getNearestNodeOfType<ListNode>(node, ListNode)
			const listNodeToUse = node instanceof ListNode ? node : parentList
			const listType = listNodeToUse?.getListType() ?? null
			setListType(listType !== "check" ? listType : null)
		})
	}, [editor])

	useEffect(() => {
		return mergeRegister(
			editor.registerUpdateListener(() => {
				update()
			}),
			editor.registerRootListener(() => {
				if (editor.getRootElement() === null) {
					setText(false)
				}
			}),
		)
	}, [editor, update])

	useEffect(() => {
		const abortController = new AbortController()

		document.addEventListener(
			"mousemove",
			event => {
				if (containerRef === null) return

				if (event.buttons === 1 || event.buttons === 3) {
					if (containerRef.style.pointerEvents !== "none") {
						const x = event.clientX
						const y = event.clientY
						const elementUnderMouse = document.elementFromPoint(x, y)

						if (!containerRef.contains(elementUnderMouse)) {
							// Mouse is not over the target element => not a normal click, but probably a drag
							containerRef.style.pointerEvents = "none"
						}
					}
				}
			},
			{ signal: abortController.signal },
		)
		document.addEventListener(
			"mouseup",
			() => {
				if (containerRef === null) return

				if (containerRef.style.pointerEvents !== "auto") {
					containerRef.style.pointerEvents = "auto"
				}
			},
			{ signal: abortController.signal },
		)

		return () => {
			abortController.abort()
		}
	}, [containerRef])

	const $updateToolbarPosition = useCallback(() => {
		if (containerRef === null) return

		const selection = $getSelection()

		const nativeSelection = window.getSelection()

		const rootElement = editor.getRootElement()
		if (
			selection !== null &&
			nativeSelection !== null &&
			!nativeSelection.isCollapsed &&
			rootElement !== null &&
			rootElement.contains(nativeSelection.anchorNode)
		) {
			let rect
			if (nativeSelection.anchorNode === rootElement) {
				let inner = rootElement
				while (inner.firstElementChild !== null) {
					inner = inner.firstElementChild as HTMLElement
				}
				rect = inner.getBoundingClientRect()
			} else {
				rect = nativeSelection.getRangeAt(0).getBoundingClientRect()
			}

			setToolbarPosition(rect, containerRef)
		}
	}, [editor, containerRef])

	useEffect(() => {
		const abortController = new AbortController()

		function updateToolbarPosition() {
			editor.getEditorState().read(() => {
				$updateToolbarPosition()
			})
		}

		window.addEventListener("resize", updateToolbarPosition, { signal: abortController.signal })

		const scrollerElement = document.body.parentElement
		if (scrollerElement) {
			scrollerElement.addEventListener("scroll", updateToolbarPosition, { signal: abortController.signal })
		}

		return () => {
			abortController.abort()
		}
	}, [editor, $updateToolbarPosition])

	useEffect(() => {
		editor.getEditorState().read(() => {
			$updateToolbarPosition()
		})
		return mergeRegister(
			editor.registerUpdateListener(({ editorState }) => {
				editorState.read(() => {
					$updateToolbarPosition()
				})
			}),

			editor.registerCommand(
				SELECTION_CHANGE_COMMAND,
				() => {
					$updateToolbarPosition()
					return false
				},
				COMMAND_PRIORITY_LOW,
			),
		)
	}, [editor, $updateToolbarPosition])

	useEffect(() => {
		if (!isText) {
			setColorPickerOpen(false)
			setLinkInputOpen(false)
		}
	}, [isText])

	function formatText(formatType: TextFormatType) {
		editor.dispatchCommand(FORMAT_TEXT_COMMAND, formatType)
	}

	function toggleHeading() {
		editor.update(() => {
			const selection = $getSelection()
			if (isHeading) {
				$setBlocksType(selection, () => $createParagraphNode())
			} else {
				$setBlocksType(selection, () => $createHeadingNode("h2"))
			}
		})
	}

	const setTextColor = useDebouncedCallback(
		(color: string | null) => {
			editor.update(() => {
				const selection = $getSelection()
				if (selection) {
					$patchStyleText(selection, { color })
				}
			})
		},
		100,
		{ maxWait: 100 },
	)

	if (!isText || !editor.isEditable()) return null

	return (
		<Portal>
			<Container ref={setContainerRef}>
				{headings && (
					<Tooltip tooltip="Heading">
						<Option $isActive={isHeading} onClick={toggleHeading}>
							<OptionIcon as={IconHeading} />
						</Option>
					</Tooltip>
				)}
				{colors && (
					<ColorOptionContainer>
						<Tooltip tooltip="Text color">
							<Option
								$isActive={isColorPickerOpen}
								onClick={() => {
									setColorPickerOpen(current => !current)
								}}
							>
								<OptionIcon as={IconTextColor} />
							</Option>
						</Tooltip>
						{isColorPickerOpen && (
							<ColorPickerContainer>
								<ColorPicker color={color ?? undefined} onChange={setTextColor} />
								<ColorInput color={color ?? undefined} onChange={setColor} prefixed />
								<ResetColorButton onClick={() => setTextColor(null)}>Reset</ResetColorButton>
							</ColorPickerContainer>
						)}
					</ColorOptionContainer>
				)}
				<Tooltip tooltip="Bold">
					<Option $isActive={isBold} onClick={() => formatText("bold")}>
						<OptionIcon as={IconBold} />
					</Option>
				</Tooltip>
				<Tooltip tooltip="Italic">
					<Option $isActive={isItalic} onClick={() => formatText("italic")}>
						<OptionIcon as={IconItalic} />
					</Option>
				</Tooltip>
				<Tooltip tooltip="Underline">
					<Option $isActive={isUnderline} onClick={() => formatText("underline")}>
						<OptionIcon as={IconUnderline} />
					</Option>
				</Tooltip>
				<Tooltip tooltip="Strikethrough">
					<Option $isActive={isStrikethrough} onClick={() => formatText("strikethrough")}>
						<OptionIcon as={IconStrikethrough} />
					</Option>
				</Tooltip>
				<Tooltip tooltip="Subscript">
					<Option $isActive={isSubscript} onClick={() => formatText("subscript")}>
						<OptionIcon as={IconSubscript} />
					</Option>
				</Tooltip>
				<Tooltip tooltip="Superscript">
					<Option $isActive={isSuperscript} onClick={() => formatText("superscript")}>
						<OptionIcon as={IconSuperscript} />
					</Option>
				</Tooltip>
				<Tooltip tooltip="Code">
					<Option $isActive={isCode} onClick={() => formatText("code")}>
						<OptionIcon as={IconCode} />
					</Option>
				</Tooltip>
				<Tooltip tooltip="Bullet list">
					<Option
						$isActive={listType === "bullet"}
						onClick={() => {
							if (listType === "bullet") {
								editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
							} else {
								editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
							}
						}}
					>
						<OptionIcon as={IconList} />
					</Option>
				</Tooltip>
				<Tooltip tooltip="Numbered list">
					<Option
						$isActive={listType === "number"}
						onClick={() => {
							if (listType === "number") {
								editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
							} else {
								editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
							}
						}}
					>
						<OptionIcon as={IconListNumbers} />
					</Option>
				</Tooltip>
				<LinkOptionContainer>
					<Tooltip tooltip="Link">
						<Option $isActive={isLink} onClick={() => setLinkInputOpen(current => !current)}>
							<OptionIcon as={IconLink} />
						</Option>
					</Tooltip>
					<LinkInputContainer $isOpen={isLinkInputOpen}>
						<LinkInput
							type="text"
							placeholder="Enter link..."
							value={linkInput}
							onChange={e => setLinkInput(e.target.value)}
						/>
						<LinkButtons>
							{isLink ? (
								<>
									<LinkButton
										$isDanger
										onClick={() => {
											editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
											setLinkInputOpen(false)
										}}
									>
										Delete
									</LinkButton>
									<LinkButton
										onClick={() => {
											editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
												url: linkInput,
												target: "_blank",
												rel: "noopener noreferrer",
											})
											setLinkInputOpen(false)
										}}
									>
										Update
									</LinkButton>
								</>
							) : (
								<LinkButton
									onClick={() => {
										editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
											url: linkInput,
											target: "_blank",
											rel: "noopener noreferrer",
										})
										setLinkInputOpen(false)
									}}
								>
									Create
								</LinkButton>
							)}
						</LinkButtons>
					</LinkInputContainer>
				</LinkOptionContainer>
			</Container>
		</Portal>
	)
}

const inputStyles = css`
	font-size: 16px;
	color: black;
	background-color: white;
	border: 1px solid #d0d5dd;
	border-radius: 8px;
	box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
	padding: 8px 12px;

	&::placeholder {
		color: #6d6d6d;
	}
`

const buttonStyles = css<{ $isDanger?: boolean }>`
	appearance: none;
	font-size: 16px;
	color: ${props => (props.$isDanger ? "white" : "black")};
	background-color: ${props => (props.$isDanger ? "#f04438" : "white")};
	border: 1px solid ${props => (props.$isDanger ? "#f04438" : "#d0d5dd")};
	border-radius: 8px;
	box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
	padding: 8px 12px;
	cursor: pointer;
`

const Container = styled.div`
	display: flex;
	background: #fff;
	padding: 4px;
	vertical-align: middle;
	position: absolute;
	top: 0;
	left: 0;
	z-index: 10000;
	opacity: 0;
	box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.3);
	border-radius: 8px;
	transition: opacity 0.5s;
	display: flex;
	flex-direction: row;
	gap: 2px;
`

const Option = styled.button.attrs({ type: "button" })<{ $isActive: boolean }>`
	appearance: none;
	text-decoration: none;
	border: none;
	font-weight: normal;
	color: black;
	text-align: left;
	margin: 0;
	display: flex;
	background: none;
	border-radius: 10px;
	padding: 8px;
	cursor: pointer;
	vertical-align: middle;

	${props =>
		props.$isActive &&
		css`
			background-color: rgba(223, 232, 250, 0.3);
		`}

	&:hover {
		background-color: #eee;
	}
`

const OptionIcon = styled.div`
	height: 18px;
	width: 18px;
	opacity: 0.6;
`

const ColorPicker = styled(HexColorPicker)`
	grid-area: picker;
`

const ColorInput = styled(HexColorInput)`
	${inputStyles}
	grid-area: input;
`

const ResetColorButton = styled.button<{ $isDanger?: boolean }>`
	${buttonStyles}
	grid-area: reset;
`

const ColorOptionContainer = styled.div`
	position: relative;
`

const ColorPickerContainer = styled.div`
	position: absolute;
	z-index: 10001;
	bottom: -4px;
	left: calc(100% + 8px);
	background-color: white;
	box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.3);
	border-radius: 10px;
	padding: 8px;
	display: grid;
	grid-template-areas: "picker input" "picker reset" "picker .";
	gap: 8px;
`

const LinkOptionContainer = styled.div`
	position: relative;
`

const LinkInputContainer = styled.div<{ $isOpen: boolean }>`
	position: absolute;
	z-index: 10001;
	top: ${34 + 4 + 4}px;
	left: 0;
	transform: ${props => `translateX(calc(-100% + 34px)) ${props.$isOpen ? "scale(1)" : "scale(0)"}`};
	transform-origin: top right;
	background-color: white;
	box-shadow: 0px 5px 10px rgba(0, 0, 0, 0.3);
	border-radius: 10px;
	padding: 8px;
	display: flex;
	flex-direction: column;
	gap: 8px;
	transition: transform 0.15s ease-in-out;
	will-change: transform;
`

const LinkInput = styled.input`
	${inputStyles}
	width: 400px;
`

const LinkButtons = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
	gap: 8px;
`

const LinkButton = styled.button<{ $isDanger?: boolean }>`
	${buttonStyles}
`

export default LexicalToolbarPlugin
